/* eslint-disable no-console */
import appConfig from 'app-config';
import _ from 'lodash';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { platformMessages } from 'ml-components-platform-react';
import { MLModalService } from 'ml-portals-services-react';

const isAgent = appConfig.portalCode === 'GPA';

const BOOKROLL_WITH_RATE_CAPPING = 'with_Rate_Capping';
const BOOKROLL_WITHOUT_RATE_CAPPING = 'without_ratecapping';

function consumeCallbacksSequentially(callbackQueue, runOnCompletion, runOnError) {
    if (callbackQueue.length > 0) {
        callbackQueue.shift()().then(
            () => consumeCallbacksSequentially(callbackQueue, runOnCompletion, runOnError),
            (err) => {
                console.error(err);
                if (runOnError) {
                    runOnError(err);
                }
            });
    } else if (runOnCompletion) {
        runOnCompletion();
    }
}

const getQuoteCoverages = (subVM, lobPathString, quote) => {
    const offerings = _.get(subVM, `lobData.${lobPathString}.offerings.value`, []);
    const selectedOffering = offerings.find((o) => o.branchCode === quote.branchCode && o.branchName === quote.branchName
        && o.branchPublicID === quote.publicID);
    return _.get(selectedOffering, 'coverages');
};

// logics from v9 common qnb flowconfig.js
function MLQuoteFlowUtil() {
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { CustomQuoteService } = useDependencies('CustomQuoteService');
    const {
        showProgressDialog,
        hideProgressDialog,
        showErrorModal
    } = MLModalService();

    const cancel = (submissionVM, lastVisitedStep, showReportModalWithSubmissionVM, authHeader) => {
        return new Promise((resolve, reject) => {
            if (isAgent) {
                if (submissionVM.isChoiceAgentSubmission_ML.value) {
                    showReportModalWithSubmissionVM(submissionVM);
                }
            }

            if (submissionVM.baseData.policyAddress.value.state === 'AK' && submissionVM.baseData.value.termType === '7Days') {
                return resolve();
            }
            if (submissionVM.bookrollInd_ML.value && (_.get(submissionVM.bookrollInd_ML.value.code) === BOOKROLL_WITH_RATE_CAPPING || 
                    submissionVM.bookrollInd_ML.value.code === BOOKROLL_WITHOUT_RATE_CAPPING)) {
                return resolve();
            }
            if (!submissionVM.quoteID.value) {
                // no submission was created so just exit without saving
                return resolve();
            }
            const isDraftStatus = submissionVM?.baseData?.status_ML?.value === 'Draft';

            if (isDraftStatus && (!submissionVM.aspects.valid || !submissionVM.aspects.subtreeValid)) {
                // invalid data when trying to exit wizard. Exiting without saving
                return resolve();
            }
            if (isDraftStatus) {
                return LoadSaveService.updateDraftSubmission(submissionVM.value, authHeader).then(resolve, reject);
            }
            if (lastVisitedStep === 'quoteDetails') {
                const lobPathString = _.camelCase(_.get(submissionVM, 'baseData.productName.value', ''));
                const selectedQuote = submissionVM.value.quoteData.offeredQuotes.find((quote) => {
                    return quote.publicID === submissionVM.value.bindData.chosenQuote;
                });

                if (!selectedQuote.quoteStatus.isStale) {
                    return resolve();
                }

                if (isAgent) {
                    const innerPromise = new Promise((innerResolve, innerReject) => {
                        const updateCallbackRequests = submissionVM.value.quoteData.offeredQuotes
                            .map((quote) => {
                                const coverages = {
                                    [`${lobPathString}`]: getQuoteCoverages(submissionVM, lobPathString, quote)
                                }
                                const updateCustomQuoteRequest = {
                                    quote,
                                    sessionUUID: submissionVM.value.sessionUUID,
                                    quoteID: submissionVM.value.quoteID,
                                    ratingPaymentMethod_ML: quote.ratingPaymentMethod_ML,
                                    ratingPaymentSchedule_ML: quote.ratingPaymentSchedule_ML,
                                    productCode_ML: submissionVM.value.baseData.productCode,
                                    forcePaymentMethodSelection_ML: true,
                                    markAsSelectedVersion_ML: quote.publicID === submissionVM.value.bindData.chosenQuote,
                                    coverages : coverages
                                };
                                return () => CustomQuoteService.updateCustomQuote(updateCustomQuoteRequest, authHeader);
                            });
            
                        consumeCallbacksSequentially(
                            updateCallbackRequests,
                            () => innerResolve(),
                            (err) => innerReject(err)
                        );
                    })
                    innerPromise.then(resolve, reject);
                    return innerPromise;
                }

                const coverages = {
                    [`${lobPathString}`]: getQuoteCoverages(submissionVM, lobPathString, selectedQuote)
                }

                const updateCustomQuoteRequest = {
                    quote: selectedQuote,
                    sessionUUID: submissionVM.value.sessionUUID,
                    quoteID: submissionVM.value.quoteID,
                    ratingPaymentMethod_ML: selectedQuote.ratingPaymentMethod_ML,
                    ratingPaymentSchedule_ML: selectedQuote.ratingPaymentSchedule_ML,
                    productCode_ML: submissionVM.value.baseData.productCode,
                    forcePaymentMethodSelection_ML: true,
                    markAsSelectedVersion_ML: true,
                    coverages : coverages
                };

                return CustomQuoteService.updateCustomQuote('quoteandbind', updateCustomQuoteRequest).then(() => {
                    return submissionVM.value;
                });
            }

            if (submissionVM.isBundle_ML &&
                submissionVM.isBundle_ML.value &&
                (lastVisitedStep === 'availableDocuments' || lastVisitedStep === 'signAndBind')) {

                return LoadSaveService.preBind(submissionVM.value, authHeader).then(resolve, reject);
            }

            if (submissionVM.aspects.valid && submissionVM.aspects.subtreeValid) {
                return LoadSaveService.updateQuotedSubmission(submissionVM.value, authHeader).then(resolve, reject);
            }

            return resolve();
        });
    };

    const returnToQuoteScreen = async (validateData, submissionVM, updateWizardData, history, authHeader, updateBeforeReturnToQuote, onReturnToQuote) => {
        const isDataValid = await validateData(true);
        if (!isDataValid) {
            return false;
        }

        _.unset(submissionVM.value, 'bindData.paymentDetails');

        try {
            let res;
            if (onReturnToQuote) {
                onReturnToQuote();
            } else if (updateBeforeReturnToQuote) {
                res = await Promise.resolve(updateBeforeReturnToQuote(submissionVM));
                _.set(submissionVM, 'value', res);
                updateWizardData(submissionVM);

                const zip = _.get(submissionVM, 'baseData.policyAddress.postalCode.value').substring(0, 5);
                history.push('/quoteentry', {quoteID: submissionVM.quoteID.value, postalCode: zip, returnToQuoteFlag: 'true'});
            } else {
                showProgressDialog(platformMessages.saveSubmission);
                try {
                    res = await LoadSaveService.updateDraftSubmission(
                        submissionVM.value,
                        authHeader
                    );

                    _.set(submissionVM, 'value', res);
                    updateWizardData(submissionVM);

                    const zip = _.get(submissionVM, 'baseData.policyAddress.postalCode.value').substring(0, 5);
                    history.push('/quoteentry', {quoteID: submissionVM.quoteID.value, postalCode: zip, returnToQuoteFlag: 'true'});
                } catch (error) {
                    hideProgressDialog();
                    if (error.message) {
                        console.log(error.message);
                    } else if (error.baseError) {
                        console.log(error.baseError);
                    } else {
                        console.log('Error occurred while trying to updateDraftSubmission on RETURN TO QUOTE');
                    }
                    showErrorModal(null, platformMessages.mlErrorOccurredWarning);
                }
            }
        } catch (error) {
            hideProgressDialog();
        }

        return true;
    };

    return {
        cancel,
        returnToQuoteScreen
    }
}

export default MLQuoteFlowUtil;